import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Trash2, Upload, Plus, HelpCircle, X } from "lucide-react";

interface Contact {
  id: string; email: string; firstName: string | null; lastName: string | null;
  custom: Record<string, unknown> | null; createdAt: string;
}

interface ListItem { id: string; name: string; description: string | null; contact_count: number }
interface ListsResp { lists: ListItem[]; total: number; totalForUser: number; maxLists: number }

const PAGE_SIZE = 50;
const LIST_PICKER_PAGE_SIZE = 10;

export default function ContactsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  function changeSearch(v: string) { setSearch(v); setPage(0); }

  const { data, isLoading } = useQuery({
    queryKey: ["contacts", search, page],
    queryFn: () => api<{ contacts: Contact[]; total: number }>(
      `/api/contacts?q=${encodeURIComponent(search)}&limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`,
    ),
  });
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  const [showAdd, setShowAdd] = useState(false);
  const [showCsvHelp, setShowCsvHelp] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newFirst, setNewFirst] = useState("");
  const [newLast, setNewLast] = useState("");
  const [selectedLists, setSelectedLists] = useState<Map<string, string>>(new Map()); // id → name

  const addMut = useMutation({
    mutationFn: (b: { email: string; firstName: string; lastName: string; listIds: string[] }) =>
      api("/api/contacts", { method: "POST", body: JSON.stringify(b) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["lists"] }); // counts changed
      setShowAdd(false); setNewEmail(""); setNewFirst(""); setNewLast(""); setSelectedLists(new Map());
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/api/contacts/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }),
  });

  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState<string | null>(null);
  async function uploadCsv(file: File) {
    setCsvBusy(true); setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/contacts/import-csv", {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem("notify-token")}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || "Upload failed");
      const unknownNote = d.unknownLists?.length ? ` · ${d.unknownLists.length} unknown list name${d.unknownLists.length === 1 ? "" : "s"}: ${d.unknownLists.join(", ")}` : "";
      setCsvResult(`Imported ${d.inserted}, skipped ${d.skipped} (duplicate), malformed ${d.malformed} (of ${d.total}). ${d.listAttachments} list attachments.${unknownNote}`);
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["lists"] });
    } catch (e) {
      setCsvResult(`Error: ${(e as Error).message}`);
    } finally {
      setCsvBusy(false);
    }
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold text-gray-900">Contacts</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowCsvHelp(true)}
            title="See CSV format and example"
            className="bg-white border border-gray-300 text-gray-600 rounded-lg w-8 h-8 inline-flex items-center justify-center hover:bg-gray-50">
            <HelpCircle size={14} />
          </button>
          <label className="bg-white border border-gray-300 text-gray-700 rounded-lg px-3 py-1.5 text-sm flex items-center gap-1.5 hover:bg-gray-50 cursor-pointer">
            <Upload size={14} /> Import CSV
            <input type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && uploadCsv(e.target.files[0])} />
          </label>
          <button onClick={() => setShowAdd(true)}
            className="bg-indigo-600 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1.5 hover:bg-indigo-700">
            <Plus size={14} /> Add contact
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-6">{data?.total ?? "—"} total. Click <HelpCircle size={11} className="inline -mt-0.5" /> for the CSV format reference.</p>

      {csvResult && <div className="mb-4 text-sm bg-blue-50 border border-blue-200 rounded p-3">{csvBusy ? "Uploading…" : csvResult}</div>}

      <input type="search" placeholder="Search…" value={search} onChange={(e) => changeSearch(e.target.value)}
        className="w-full max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:border-indigo-500" />

      {showAdd && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
              <input placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
              <input placeholder="optional" value={newFirst} onChange={(e) => setNewFirst(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last name</label>
              <input placeholder="optional" value={newLast} onChange={(e) => setNewLast(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
            </div>
          </div>

          <ListPicker selected={selectedLists} onChange={setSelectedLists} />

          {addMut.error && <p className="text-xs text-red-600 mt-2">{(addMut.error as Error).message}</p>}
          <div className="mt-3 flex gap-2">
            <button onClick={() => addMut.mutate({
              email: newEmail, firstName: newFirst, lastName: newLast,
              listIds: Array.from(selectedLists.keys()),
            })} disabled={!newEmail || addMut.isPending}
              className="bg-indigo-600 text-white px-3 py-1.5 rounded text-sm hover:bg-indigo-700 disabled:opacity-50">
              {addMut.isPending ? "Adding…" : `Add${selectedLists.size > 0 ? ` to ${selectedLists.size} list${selectedLists.size === 1 ? "" : "s"}` : ""}`}
            </button>
            <button onClick={() => { setShowAdd(false); setSelectedLists(new Map()); }} className="text-sm text-gray-500 hover:text-gray-700 px-2">Cancel</button>
          </div>
        </div>
      )}

      {isLoading ? <p className="text-sm text-gray-500">Loading…</p> : null}
      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Added</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(data?.contacts ?? []).map((c) => (
              <tr key={c.id} className="border-t border-gray-100">
                <td className="px-4 py-2 font-mono text-xs">{c.email}</td>
                <td className="px-4 py-2">{[c.firstName, c.lastName].filter(Boolean).join(" ") || "—"}</td>
                <td className="px-4 py-2 text-xs text-gray-500">{new Date(c.createdAt).toLocaleDateString()}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => { if (confirm(`Delete ${c.email}?`)) deleteMut.mutate(c.id); }}
                    className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && data?.contacts.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">No contacts. Add one above or import a CSV.</p>
        )}

        {total > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <div>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</div>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Prev</button>
              <button onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
                className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {showCsvHelp && <CsvHelpModal onClose={() => setShowCsvHelp(false)} />}
    </div>
  );
}

function ListPicker({ selected, onChange }: {
  selected: Map<string, string>;
  onChange: (m: Map<string, string>) => void;
}) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  function changeQ(v: string) { setQ(v); setPage(0); }

  const { data } = useQuery({
    queryKey: ["lists", "picker", q, page],
    queryFn: () => api<ListsResp>(`/api/lists?q=${encodeURIComponent(q)}&limit=${LIST_PICKER_PAGE_SIZE}&offset=${page * LIST_PICKER_PAGE_SIZE}`),
  });
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / LIST_PICKER_PAGE_SIZE) - 1);

  function toggle(id: string, name: string) {
    const m = new Map(selected);
    if (m.has(id)) m.delete(id); else m.set(id, name);
    onChange(m);
  }

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Add to lists (optional)</label>

      {/* Selected chips */}
      {selected.size > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {Array.from(selected.entries()).map(([id, name]) => (
            <span key={id} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5 text-xs">
              {name}
              <button type="button" onClick={() => toggle(id, name)} className="hover:bg-indigo-200 rounded-full p-0.5">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      <input type="search" placeholder="Search lists…" value={q} onChange={(e) => changeQ(e.target.value)}
        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-2" />

      <div className="border border-gray-200 rounded max-h-52 overflow-y-auto">
        {(data?.lists ?? []).map((l) => (
          <label key={l.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 text-sm cursor-pointer">
            <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id, l.name)} />
            <span className="flex-1 truncate">{l.name}</span>
            <span className="text-xs text-gray-400 shrink-0">{l.contact_count} contact{l.contact_count === 1 ? "" : "s"}</span>
          </label>
        ))}
        {data && data.lists.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-3">{q ? `No lists match "${q}".` : "No lists yet — create one in the Lists page first."}</p>
        )}
      </div>

      {total > LIST_PICKER_PAGE_SIZE && (
        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <div>{page * LIST_PICKER_PAGE_SIZE + 1}–{Math.min((page + 1) * LIST_PICKER_PAGE_SIZE, total)} of {total}</div>
          <div className="flex gap-1">
            <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              className="px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Prev</button>
            <button type="button" onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
              className="px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CsvHelpModal({ onClose }: { onClose: () => void }) {
  const example = `email,first_name,last_name,lists,company
alice@example.com,Alice,Smith,Newsletter;Beta users,Acme Co
bob@example.com,Bob,Jones,Newsletter,Foo Corp
carol@example.com,Carol,,, `;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">CSV import format</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 text-sm text-gray-700">
          <p className="mb-3">Standard CSV with a header row on the first line. Values containing commas or quotes need to be wrapped in double quotes (Excel does this automatically when you Save As CSV).</p>

          <h4 className="font-semibold text-gray-900 mt-4 mb-1">Required column</h4>
          <ul className="text-xs space-y-0.5 mb-3">
            <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">email</code> — recipient address. Lower-cased on import. Duplicates within your contacts are skipped.</li>
          </ul>

          <h4 className="font-semibold text-gray-900 mt-4 mb-1">Optional columns</h4>
          <ul className="text-xs space-y-0.5 mb-3">
            <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">first_name</code> — used by the <code>{"{{first_name}}"}</code> merge tag.</li>
            <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">last_name</code> — used by the <code>{"{{last_name}}"}</code> merge tag.</li>
            <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">lists</code> (or <code>list</code>) — semicolon-separated list NAMES the contact should be added to. Lists must already exist (create them first in the Lists page); unknown names are reported back to you. Use <code>;</code> not <code>,</code> because the comma is the CSV delimiter.</li>
          </ul>

          <h4 className="font-semibold text-gray-900 mt-4 mb-1">Anything else</h4>
          <p className="text-xs mb-3">Other columns become custom merge fields. A <code className="bg-gray-100 px-1.5 py-0.5 rounded">company</code> column → usable as <code>{"{{custom.company}}"}</code> in templates.</p>

          <h4 className="font-semibold text-gray-900 mt-4 mb-1">Example</h4>
          <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto">{example}</pre>
          <p className="text-xs text-gray-500 mt-2">
            (Carol has no first/last/lists/company → she's added with just the email. Empty cells are fine.)
          </p>

          <h4 className="font-semibold text-gray-900 mt-4 mb-1">After upload</h4>
          <p className="text-xs">You'll see a count of <em>imported</em> (new), <em>skipped</em> (duplicate emails you already have), <em>malformed</em> (rows where email was missing or invalid), and how many list memberships were created.</p>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700">Got it</button>
        </div>
      </div>
    </div>
  );
}
